'use client'

import { FC, useEffect, useState, useRef, PropsWithChildren } from 'react'
import styles from './LegalWrapper.module.scss'
import { WarrantyProps } from './types'
import cx from 'classnames'
import { Icon } from 'components/Phantom/Icon'
import { useHeadlineObserver } from 'components/_hooks/useHeadlineObserver'
import { Img } from 'components/basic/Img'
import React from 'react'
import { Button } from 'components/Phantom/Button'

interface TOCProps {
	headers: Element[]
	defaultCollapse?: boolean
}
interface ElementTree {
	id?: string
	children: ElementTree[]
}
interface HeaderInfo {
	title: string
	collapsed: boolean
	level: number
}
const goToElement = (id) => {
	document.getElementById(id).scrollIntoView({ behavior: 'smooth' })
}
const TOC = ({ headers, defaultCollapse = true }: TOCProps) => {
	const [elementTree, setElementTree] = useState<ElementTree[]>([])
	const [elements, setElements] = useState<Record<string, HeaderInfo>>()
	const { activeId } = useHeadlineObserver('toc-content')

	function createForest(flatdata: Element[]) {
		if (!flatdata) {
			return [] as ElementTree[]
		}
		const path = [{ id: null, children: [] as ElementTree[] }]
		const depthMap = { 0: 0 }
		for (const obj of flatdata) {
			path[(depthMap[obj.nodeName.charAt(1)] ??= path.length) - 1].children.push((path[depthMap[obj.nodeName.charAt(1)]] = { id: obj.id, children: [] }))
		}
		return path[0].children
	}
	function enrichData(flatdata: Element[]) {
		const elements = {}
		for (const element of flatdata) {
			elements[element.id] = { collapsed: defaultCollapse, active: false, title: element.getAttribute('data-alternate-title') || element.textContent, level: Number(element.nodeName.charAt(1)) }
		}
		return elements
	}
	useEffect(() => {
		if (headers) {
			setElementTree(createForest(headers))
			setElements(enrichData(headers))
		}
	}, [headers])

	const isActive = (id: string, children: ElementTree[]) => {
		if (activeId !== id && elements[id].collapsed && children.length > 0) {
			return !!children.find((element) => element.id === activeId)
		}
		return activeId === id
	}

	const RenderTree = ({ collapsed, tree }: { collapsed: boolean; tree: ElementTree[] }) => {
		return (
			<div className={cx(styles.toc_h_wrapper, collapsed ? styles.toc_h_wrapper_collapsed : styles.toc_h_wrapper_expanded)}>
				{tree?.map(({ id, children }) => (
					<React.Fragment key={id}>
						<div className={cx(styles.toc_line, styles.toc_line, isActive(id, children) ? styles.toc_line_active : '', styles[`toc_level_${elements[id].level}`])}>
							<Button.Empty
								id={`toc-link-${id}`}
								className={styles.toc_link}
								onClick={() => goToElement(id)}
							>
								{elements[id].title}
							</Button.Empty>
							{children.length > 0 ? (
								<Button.Empty
									id={`toc-collapse-${id}`}
									className={styles.collapse_icon}
									onClick={() => {
										const toggleValue = !elements[id].collapsed
										setElements((elements) => {
											elements[id].collapsed = toggleValue
											return { ...elements }
										})
									}}
								>
									<Icon
										color="black"
										className={styles.toc_link}
										name={elements[id].collapsed ? 'PlusDark' : 'MinusDark'}
									/>
								</Button.Empty>
							) : (
								''
							)}
						</div>
						{children.length > 0 && (
							<RenderTree
								collapsed={elements[id].collapsed}
								tree={children}
							/>
						)}
					</React.Fragment>
				))}
			</div>
		)
	}
	return (
		<nav className={styles.toc_container}>
			{headers?.length > 1 && (
				<RenderTree
					collapsed={false}
					tree={elementTree}
				/>
			)}
		</nav>
	)
}
export const ContactCard: FC<PropsWithChildren> = (props) => {
	return <div className={styles.contact_card}>{props.children}</div>
}

export const LegalWrapper: FC<PropsWithChildren & WarrantyProps> = (props) => {
	const contentRef = useRef<HTMLDivElement>(null)
	const [headings, setHeadings] = useState<Element[]>()
	const { includeContact = false, showChevron = true } = props
	useEffect(() => {
		const elements = Array.from(contentRef?.current?.querySelectorAll('h2,h3,h4'))
		setHeadings(elements)
	}, [])
	return (
		<div
			ref={contentRef}
			className={styles.container}
		>
			<div className={styles.hero_img}>
				<Img
					src={props.heroSrc}
					alt={`Hero for ${props.mainTitle}`}
					dprHeight={2000}
				/>
				<div className={styles.subheader_float}>
					<div className={styles.main_title}>{props.mainTitle}</div>
					{props.subHeaderElement}
				</div>
				{showChevron && (
					<Button.Empty
						id="hero-chevron"
						onClick={() => goToElement('toc-content')}
						className={styles.hero_chevron}
					>
						<Icon name="ChevronDownDark"></Icon>
					</Button.Empty>
				)}
			</div>
			<div className={styles.content}>
				<div className={styles.left_panel}>
					<TOC headers={headings} />
				</div>
				<div
					className={styles.right_panel}
					id="toc-content"
				>
					{props.children}
					{includeContact && (
						<ContactCard>
							<h2 id="contactusfooter">Contact Us</h2>
							<p>
								<span>You can email us with any questions or comments at:</span>
								<br />
								<a
									href="mailto:support@eightsleep.com"
									target="_blank"
									rel="noopener noreferrer"
								>
									<span>support@eightsleep.com</span>
								</a>
							</p>

							<p>
								<span>Privacy Officer:</span>
							</p>
							<p>
								<strong>
									Eight Sleep, Inc.
									<br />
									164 W 25th Street, 9th Floor
									<br />
									New York, NY 10001
								</strong>
							</p>
						</ContactCard>
					)}
				</div>
			</div>
		</div>
	)
}
